import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

// ─────────────────────────────────────────────────────────────────────────────
// UniversityImage — official campus / hero image for a university card.
//
// Resolution order:
//   1. The backend endpoint `/api/universities/{slug}/og-image` returns the
//      official Open-Graph image URL from the university's own website,
//      cached server-side for 30 days.
//   2. If the endpoint returns null OR the resolved <img> fails to load,
//      we render a branded fallback: the university's initials inside a
//      soft indigo gradient.
//
// Behaviour:
//   • Lazy-loads via native `loading="lazy"`.
//   • Reserves space via aspect-ratio so layout never shifts.
//   • Module-level cache de-dupes identical fetches inside one page load
//     (e.g. an Atlas grid with 12 cards triggers 12 distinct API calls
//     but each unique slug is fetched only once).
// ─────────────────────────────────────────────────────────────────────────────

const _resolveCache = new Map(); // slug -> Promise<string | null>

function fetchImageUrl(slug) {
  if (_resolveCache.has(slug)) return _resolveCache.get(slug);
  const p = api
    .get(`/universities/${slug}/og-image`)
    .then((r) => r.data?.image_url ?? null)
    .catch(() => null);
  _resolveCache.set(slug, p);
  return p;
}

function initialsOf(name) {
  if (!name) return "U";
  // Strip leading "The ", grab capitalised initials, cap at 2 chars.
  const cleaned = name.replace(/^The\s+/i, "").trim();
  const tokens = cleaned.split(/\s+/);
  const letters = tokens
    .filter((t) => /^[A-Za-z]/.test(t))
    .slice(0, 2)
    .map((t) => t[0].toUpperCase());
  return letters.join("") || cleaned.slice(0, 2).toUpperCase();
}

export default function UniversityImage({
  slug,
  name,
  aspectClass = "aspect-[16/9]",
  rounded = "rounded-[var(--r16)]",
  className = "",
  showGradientOverlay = false,
  testid,
}) {
  const [src, setSrc] = useState(null);
  const [state, setState] = useState("loading"); // "loading" | "ready" | "fallback"
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    setState("loading");
    setSrc(null);
    if (!slug) {
      setState("fallback");
      return;
    }
    fetchImageUrl(slug).then((url) => {
      if (!mounted.current) return;
      if (url) {
        setSrc(url);
        setState("ready");
      } else {
        setState("fallback");
      }
    });
    return () => {
      mounted.current = false;
    };
  }, [slug]);

  const showFallback = state === "fallback";
  const showSkeleton = state === "loading";
  const showImage = state === "ready" && !!src;

  return (
    <div
      className={`relative ${aspectClass} ${rounded} overflow-hidden bg-[var(--ind-soft)] ${className}`}
      data-testid={testid || `uni-img-${slug}`}
    >
      {/* Skeleton shimmer while resolving */}
      {showSkeleton && (
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-slate-50 to-indigo-50 animate-pulse" />
      )}

      {/* Branded fallback — initials on soft indigo gradient */}
      {showFallback && (
        <UniversityFallback name={name} />
      )}

      {/* Official image */}
      {showImage && (
        <img
          src={src}
          alt={name ? `${name} campus` : "University campus"}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
          onError={() => setState("fallback")}
        />
      )}

      {/* Optional gradient overlay for readability of overlaid text */}
      {showGradientOverlay && (showImage || showFallback) && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/0 to-black/0 pointer-events-none" />
      )}
    </div>
  );
}

function UniversityFallback({ name }) {
  const initials = initialsOf(name);
  return (
    <div className="absolute inset-0 bg-gradient-to-br from-indigo-100 via-indigo-200 to-indigo-300 flex items-center justify-center">
      <div className="text-white/95 font-semibold tracking-tight select-none drop-shadow-sm" style={{ fontSize: "clamp(2rem, 8cqi, 4.5rem)" }}>
        {initials}
      </div>
      {/* Subtle decorative glyph in the corner */}
      <div className="absolute right-3 bottom-3 text-white/30 text-[10px] font-semibold uppercase tracking-[0.12em]">
        Unimora
      </div>
    </div>
  );
}
